import { cornerRadius } from "../native/corners";
import type { SceneNode } from "../scene/model";
import type { AffineMatrix } from "../geometry/affine";
import { shapeStyle, strokeRadius } from "../native/appearance";
import { curveSegments, strokeTriangles } from "../native/stroke";

export interface GLMesh {
  buffer: WebGLBuffer;
  count: number;
}
type Draw = (
  matrix: AffineMatrix,
  color: string,
  opacity: number,
  node?: SceneNode,
  mesh?: GLMesh,
) => void;
/** Stencil unions round stroke segments, then applies object opacity once per sample. */
export class NativeStrokeRenderer {
  private meshes = new Map<string, GLMesh>();
  private used = new Set<string>();
  constructor(private gl: WebGL2RenderingContext) {}
  begin() {
    this.used.clear();
  }
  draw(
    node: SceneNode,
    matrix: AffineMatrix,
    pixelsPerUnit: number,
    draw: Draw,
  ) {
    const radius = strokeRadius(node);
    if (!radius) return false;
    const gl = this.gl,
      style = shapeStyle(node),
      w = node.geometry.width!,
      h = node.geometry.height!;
    const stretch = Math.hypot(
      matrix[0] / w,
      matrix[1] / w,
      matrix[2] / h,
      matrix[3] / h,
    );
    const ellipseSegments =
      node.kind === "ellipse" || cornerRadius(node) > 0
        ? curveSegments(
            (Math.max(w, h) / 2) * stretch * pixelsPerUnit,
            32,
            2048,
          )
        : 0;
    const joinSegments = curveSegments(
      radius * stretch * pixelsPerUnit,
      12,
      128,
    );
    const polygonTolerance = 0.15 / Math.max(stretch * pixelsPerUnit, 1e-8);
    const key = JSON.stringify([
      node.kind,
      node.geometry,
      style.lineStyle,
      style.strokeDash,
      radius,
      ellipseSegments,
      joinSegments,
      polygonTolerance,
    ]);
    this.used.add(key);
    let mesh = this.meshes.get(key);
    if (!mesh) {
      const triangles = strokeTriangles(
        node,
        ellipseSegments,
        joinSegments,
        polygonTolerance,
      );
      mesh = { buffer: gl.createBuffer()!, count: triangles.length / 2 };
      gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer);
      gl.bufferData(gl.ARRAY_BUFFER, triangles, gl.STATIC_DRAW);
      this.meshes.set(key, mesh);
    }
    gl.enable(gl.STENCIL_TEST);
    try {
      gl.stencilMask(0xff);
      gl.clearStencil(0);
      gl.clear(gl.STENCIL_BUFFER_BIT);
      gl.stencilFunc(gl.ALWAYS, 1, 0xff);
      gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
      gl.colorMask(false, false, false, false);
      draw(matrix, "#ffffff", 1, undefined, mesh);
      gl.colorMask(true, true, true, true);
      gl.stencilMask(0);
      gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
      if (style.fillEnabled) {
        gl.stencilFunc(gl.NOTEQUAL, 1, 0xff);
        draw(matrix, style.fill, style.opacity, node);
      }
      gl.stencilFunc(gl.EQUAL, 1, 0xff);
      const sx = 1 + (radius * 2) / w,
        sy = 1 + (radius * 2) / h;
      draw(
        [
          matrix[0] * sx,
          matrix[1] * sx,
          matrix[2] * sy,
          matrix[3] * sy,
          matrix[4],
          matrix[5],
        ],
        style.stroke,
        style.opacity,
      );
    } finally {
      gl.colorMask(true, true, true, true);
      gl.stencilMask(0xff);
      gl.disable(gl.STENCIL_TEST);
    }
    return true;
  }
  end() {
    for (const [key, mesh] of this.meshes)
      if (!this.used.has(key)) {
        this.gl.deleteBuffer(mesh.buffer);
        this.meshes.delete(key);
      }
  }
  dispose() {
    for (const mesh of this.meshes.values()) this.gl.deleteBuffer(mesh.buffer);
    this.meshes.clear();
  }
}
